"use client"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react"
import { AuthenticationRequiredError, loadUserView, UserViewRequestError } from "../lib/api-client.ts"
import { consumerBackFallback } from "../lib/consumer-history.ts"
import { isConsumerTabPath } from "../lib/consumer-navigation.ts"
import { type ConsumerDestination, consumerDestinations, type UserRoute, userRouteManifest } from "../lib/routes.ts"
import type { UserView } from "../lib/user-view.ts"
import { ConsumerTabLink } from "./consumer-tab-link.tsx"
import { Icon } from "./icons.tsx"
import { NativeWebViewBridge } from "./native-webview-bridge.tsx"
import { PageMotion } from "./page-motion.tsx"
import { RedesignIcon } from "./redesign-icon.tsx"

type AppShellBaseProps = {
  readonly appSurface?: boolean
  readonly children: ReactNode
  readonly compact?: boolean
  readonly destination: ConsumerDestination
  readonly homeBalanceOverride?: string
  readonly title?: string
  readonly unreadNotificationsOverride?: boolean
}
type AppShellProps = AppShellBaseProps &
  (
    | {
        readonly authenticated: false
        readonly routeId?: never
      }
    | {
        readonly authenticated?: true
        readonly routeId: UserRoute["id"]
      }
  )
type HomeHeaderBalance = {
  readonly value: string
}
type HomeHeaderBalancePublisher = (balance: HomeHeaderBalance | null) => void
type UnreadNotificationsPublisher = (hasUnread: boolean) => void
const HomeHeaderBalanceContext = createContext<HomeHeaderBalancePublisher | null>(null)
const UnreadNotificationsContext = createContext<UnreadNotificationsPublisher | null>(null)
const homeRoute = userRouteManifest.find(({ id }) => id === "APP-HOME-001")
const notificationsRoute = userRouteManifest.find(({ id }) => id === "APP-NOTI-001")
export const homeHeaderBalanceFromView = (view: UserView): HomeHeaderBalance | null => {
  switch (view.kind) {
    case "home": {
      if (view.failureCause !== undefined || view.state !== "DEFAULT") return null
      const available = view.details.find(({ label }) => label === "사용 가능")
      return available === undefined ? null : { value: available.value }
    }
    case "catalog":
    case "commerce":
    case "history":
    case "notifications":
    case "page":
    case "preferences":
    case "product":
    case "support":
    case "transaction":
    case "voucher":
      return null
  }
}
export const unreadNotificationsFromView = (view: UserView): boolean => view.kind === "notifications" && view.notifications.some((notification) => !notification.read)
export const useHomeHeaderBalancePublisher = (): HomeHeaderBalancePublisher => {
  const publisher = useContext(HomeHeaderBalanceContext)
  if (publisher === null) throw new TypeError("HOME_HEADER_BALANCE_REQUIRES_APP_SHELL")
  return publisher
}
export const useUnreadNotificationsPublisher = (): UnreadNotificationsPublisher => {
  const publisher = useContext(UnreadNotificationsContext)
  if (publisher === null) throw new TypeError("UNREAD_NOTIFICATIONS_REQUIRES_APP_SHELL")
  return publisher
}
const BrandLockup = () => <span className="brand-wordmark">캐시허브</span>
export const AppShell = (props: AppShellProps) => {
  const pathname = usePathname()
  const router = useRouter()
  const back = () => {
    if (window.__cashhubHandleNativeBack?.() !== true) router.replace(consumerBackFallback(pathname ?? "/home"), { scroll: false })
  }
  const { appSurface = false, children, compact = false, destination, homeBalanceOverride, unreadNotificationsOverride } = props
  const authenticated = props.authenticated !== false
  const subpage = authenticated && appSurface && pathname !== null && !isConsumerTabPath(pathname)
  const routeId = props.authenticated === false ? null : props.routeId
  const [homeHeaderBalance, publishHomeHeaderBalance] = useState<HomeHeaderBalance | null>(homeBalanceOverride === undefined ? null : { value: homeBalanceOverride })
  const [hasUnreadNotifications, publishUnreadNotificationsState] = useState<boolean | null>(unreadNotificationsOverride === undefined ? null : unreadNotificationsOverride)
  const publishUnreadNotifications = useCallback<UnreadNotificationsPublisher>((hasUnread) => {
    publishUnreadNotificationsState(hasUnread)
  }, [])
  const needsSupplementalHomeBalance = authenticated && routeId !== null && routeId !== "APP-HOME-001" && homeBalanceOverride === undefined && homeHeaderBalance === null
  const needsUnreadNotificationsLookup = authenticated && routeId !== null && routeId !== "APP-NOTI-001" && unreadNotificationsOverride === undefined
  const visibleHomeBalance = authenticated ? homeHeaderBalance : null
  const visibleHomeBalanceAmount = visibleHomeBalance?.value.replace(/\s*(?:포인트|원)$/, "") ?? null
  const showUnreadDot = hasUnreadNotifications === true
  const notificationAriaLabel = showUnreadDot ? "읽지 않은 알림이 있습니다. 알림으로 이동" : "알림으로 이동"
  useEffect(() => {
    if (!needsSupplementalHomeBalance) return
    if (homeRoute === undefined) throw new TypeError("HOME_ROUTE_REQUIRED")
    const controller = new AbortController()
    void loadUserView(homeRoute, "/home", new URLSearchParams(), controller.signal)
      .then((view) => {
        const balance = homeHeaderBalanceFromView(view)
        if (balance !== null) publishHomeHeaderBalance(balance)
      })
      .catch((error: unknown) => {
        if (error instanceof AuthenticationRequiredError || error instanceof UserViewRequestError || error instanceof TypeError) return
        if (error instanceof Error && (error.name === "AbortError" || error.name === "TimeoutError")) return
        throw error
      })
    return () => controller.abort()
  }, [needsSupplementalHomeBalance])
  useEffect(() => {
    if (!needsUnreadNotificationsLookup) return
    if (notificationsRoute === undefined) throw new TypeError("NOTIFICATIONS_ROUTE_REQUIRED")
    const controller = new AbortController()
    void loadUserView(notificationsRoute, "/notifications", new URLSearchParams(), controller.signal)
      .then((view) => {
        publishUnreadNotifications(unreadNotificationsFromView(view))
      })
      .catch((error: unknown) => {
        if (error instanceof AuthenticationRequiredError || error instanceof UserViewRequestError || error instanceof TypeError) return
        if (error instanceof Error && (error.name === "AbortError" || error.name === "TimeoutError")) return
        throw error
      })
    return () => controller.abort()
  }, [needsUnreadNotificationsLookup, publishUnreadNotifications])
  return (
    <HomeHeaderBalanceContext value={publishHomeHeaderBalance}>
      <UnreadNotificationsContext value={publishUnreadNotifications}>
        <div
          className={`app-shell ${authenticated ? "app-shell-authenticated" : "app-shell-unauthenticated"}${compact ? " app-shell-compact" : ""}`}
          data-consumer-app={appSurface ? "true" : undefined}
          data-connection-flow={pathname === "/entry" || pathname === "/login" || pathname?.startsWith("/transfer/") || pathname?.startsWith("/onboarding/") ? "true" : undefined}
          data-app-subpage={subpage ? "true" : undefined}
          onDragStartCapture={
            appSurface
              ? (event) => {
                  const target = event.target
                  if (target instanceof HTMLElement && (target.isContentEditable || target.closest("input, textarea") !== null)) return
                  event.preventDefault()
                }
              : undefined
          }
          onContextMenuCapture={
            appSurface
              ? (event) => {
                  const target = event.target
                  if (target instanceof HTMLElement && (target.isContentEditable || target.closest("input, textarea") !== null)) return
                  if (target instanceof Element && target.closest("a, img") !== null) event.preventDefault()
                }
              : undefined
          }
        >
          <NativeWebViewBridge />
          <a className="skip-link" href="#main-content">
            본문으로 건너뛰기
          </a>
          <header className="topbar">
            <div className="topbar-inner">
              {subpage ? (
                <>
                  <button aria-label="뒤로가기" className="app-back-button" onClick={back} type="button">
                    <Icon name="back" />
                  </button>
                  <h1 className="app-subpage-title">{pathname === "/balance" || pathname === "/balance/history" ? "잔액 및 이용 내역" : props.title}</h1>
                  <span aria-hidden="true" className="app-back-spacer" />
                </>
              ) : (
                <>
                  {authenticated ? (
                    <ConsumerTabLink aria-label="홈으로 이동" className="brand" href="/home">
                      <BrandLockup />
                    </ConsumerTabLink>
                  ) : (
                    <span className="brand">
                      <BrandLockup />
                    </span>
                  )}
                  {authenticated ? (
                    <div className="topbar-actions">
                      {visibleHomeBalanceAmount === null ? null : (
                        <ConsumerTabLink aria-label={`사용 가능 포인트 ${visibleHomeBalanceAmount}, 홈으로 이동`} className="home-point-summary" href="/home">
                          <span aria-hidden="true" className="home-point-symbol">
                            C
                          </span>
                          <span className="home-point-copy">
                            <small>내 포인트</small>
                            <strong>
                              <span className="home-point-amount">{visibleHomeBalanceAmount}</span>
                              <span className="home-point-suffix">P</span>
                            </strong>
                          </span>
                        </ConsumerTabLink>
                      )}
                      <Link aria-label={notificationAriaLabel} className="icon-button" data-unread={showUnreadDot ? "true" : "false"} href="/notifications">
                        <RedesignIcon name="notice" />
                        {showUnreadDot ? <span aria-hidden="true" className="notification-unread-dot" /> : null}
                      </Link>
                    </div>
                  ) : (
                    <span aria-hidden="true" className="topbar-spacer" />
                  )}
                </>
              )}
            </div>
          </header>
          <div className="app-frame">
            {authenticated && !subpage ? (
              <nav aria-label="주요 메뉴" className="bottom-nav">
                {consumerDestinations.map((item) => (
                  <ConsumerTabLink aria-current={destination === item.id ? "page" : undefined} href={item.href} key={item.id}>
                    {<RedesignIcon name={item.id === "home" ? "home" : item.id === "use" ? "shop" : "account"} />}
                    <span>{item.label}</span>
                  </ConsumerTabLink>
                ))}
              </nav>
            ) : null}
            <PageMotion>{children}</PageMotion>
          </div>
        </div>
      </UnreadNotificationsContext>
    </HomeHeaderBalanceContext>
  )
}
